import { useEffect, useRef, useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { copyTextToClipboard } from '@/lib/clipboard'
import { showApiSuccessToast } from '@/lib/api-toast'
import { cn } from '@/lib/utils'

interface DepositCopyFieldProps {
	label: string
	value: string
	className?: string
}

export function DepositCopyField({ label, value, className }: DepositCopyFieldProps) {
	const [isCopied, setIsCopied] = useState(false)
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current)
			}
		}
	}, [])

	const handleCopy = () => {
		if (!value) {
			return
		}

		void copyTextToClipboard(value).then((copied) => {
			if (!copied) {
				return
			}

			showApiSuccessToast('Copied to clipboard')
			setIsCopied(true)

			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current)
			}

			timeoutRef.current = setTimeout(() => {
				setIsCopied(false)
			}, 1400)
		})
	}

	return (
		<div className={cn('space-y-2', className)}>
			<p className='text-center text-sm font-medium tracking-[-0.21px] text-muted-foreground'>{label}</p>
			<div className='flex items-center gap-3 rounded-xl border border-border bg-secondary px-4 py-3'>
				<p className='min-w-0 flex-1 break-all text-sm font-semibold tracking-[-0.21px] text-foreground'>{value || '-'}</p>
				<button
					type='button'
					onClick={handleCopy}
					disabled={!value}
					className='flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-foreground transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40'
					aria-label={`Copy ${label}`}
				>
					{isCopied ? <Check className='h-5 w-5 text-primary' /> : <Copy className='h-5 w-5' />}
				</button>
			</div>
		</div>
	)
}
